import { useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { fetchBlogs, fetchBlogCategories } from "../../api/public.api.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { SEO } from "../../lib/seo.jsx";
import { formatDate } from "../../lib/format.js";
import { cn } from "../../lib/cn.js";

const PAGE_SIZE = 9;

/**
 * /blog — paginated blog list with category chip filter.
 * /blog/category/:slug — same view scoped to a category.
 */
export default function BlogListPage() {
  const { slug: routeCategory } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? 1);

  const setPage = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 1) sp.delete("page");
    else sp.set("page", String(next));
    setSearchParams(sp);
  };

  // Reset to page 1 when category changes
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeCategory]);

  const categoriesQ = useQuery({
    queryKey: ["public", "blog-categories"],
    queryFn: fetchBlogCategories,
    staleTime: 5 * 60_000,
  });

  const blogsQ = useQuery({
    queryKey: ["public", "blogs", { routeCategory, page }],
    queryFn: () =>
      fetchBlogs({
        page,
        limit: PAGE_SIZE,
        ...(routeCategory && { category: routeCategory }),
      }),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const meta = blogsQ.data?.meta;
  const blogs = blogsQ.data?.items ?? [];
  const activeCategory = categoriesQ.data?.find((c) => c.slug === routeCategory);

  return (
    <>
      <SEO
        title={activeCategory ? `${activeCategory.name} — Blog` : "Blog"}
        description={
          activeCategory?.description ??
          "Insights, design inspiration, and construction tips from the Shere-e-Punjab Homes team."
        }
      />

      <PageHero
        eyebrow="Blog"
        title={activeCategory?.name ?? "Stories from the build"}
        description={
          activeCategory?.description ??
          "Design inspiration, construction tips, and first-home-buyer guides — written by the team you're building with."
        }
        breadcrumbs={
          activeCategory ? [{ label: "Blog", to: "/blog" }, { label: activeCategory.name }] : null
        }
      />

      <Container className="py-12 lg:py-16">
        {/* Category chips */}
        {(categoriesQ.data?.length ?? 0) > 0 && (
          <div className="mb-10 flex flex-wrap items-center gap-2">
            <Link
              to="/blog"
              className={cn(
                "rounded-full border px-4 py-1.5 text-sm transition-colors",
                !routeCategory
                  ? "border-accent bg-accent/15 text-accent"
                  : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary"
              )}
            >
              All posts
            </Link>
            {categoriesQ.data.map((c) => (
              <Link
                key={c._id ?? c.slug}
                to={`/blog/category/${c.slug}`}
                className={cn(
                  "rounded-full border px-4 py-1.5 text-sm transition-colors",
                  routeCategory === c.slug
                    ? "border-accent bg-accent/15 text-accent"
                    : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary"
                )}
              >
                {c.name}
              </Link>
            ))}
          </div>
        )}

        {blogsQ.isLoading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 animate-pulse rounded-2xl bg-bg-elevated" />
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            No posts yet in this category.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {blogs.map((post) => (
                <BlogCard key={post._id ?? post.slug} post={post} />
              ))}
            </div>

            {meta && meta.totalPages > 1 && (
              <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
                <button
                  type="button"
                  disabled={!meta.hasPrev}
                  onClick={() => setPage(meta.page - 1)}
                  className="btn-ghost border border-border-subtle disabled:opacity-30"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-4 py-2 text-sm text-text-secondary">
                  Page <span className="text-text-primary">{meta.page}</span> of {meta.totalPages}
                </span>
                <button
                  type="button"
                  disabled={!meta.hasNext}
                  onClick={() => setPage(meta.page + 1)}
                  className="btn-ghost border border-border-subtle disabled:opacity-30"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </nav>
            )}
          </>
        )}
      </Container>
    </>
  );
}

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-card-hover"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-bg-elevated-2">
        <img
          src={imageOrPlaceholder(post.heroImage, {
            pool: "blog",
            key: post.slug ?? post._id ?? post.title,
          })}
          onError={imgErrorFallback(post.slug ?? post._id ?? post.title)}
          alt={post.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {post.category?.name && (
          <span className="absolute left-4 top-4 rounded-full bg-bg-base/80 px-3 py-1 text-xs text-accent backdrop-blur">
            {post.category.name}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl leading-snug text-text-primary group-hover:text-accent">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-2 line-clamp-3 text-sm text-text-secondary">{post.excerpt}</p>
        )}
        <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-text-muted">
          {post.publishedAt && <span>{formatDate(post.publishedAt)}</span>}
          {post.readingTime && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {post.readingTime} min read
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
